import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, Loader2, RefreshCw, CheckCircle2, XCircle, AlertTriangle,
  Clock, Package, User, ArrowRight, Ban, Search, CalendarDays, FileDown,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Feb 2026 — Stock Adjustment Approval Queue.
 *
 * Approvers (super admin OR `warehouse.stock_adjust_approve`) review
 * pending stock-adjustment requests submitted via the request-flow
 * on /admin/stock-adjust. Approve → auto-applies via the same
 * placement-edit engine (audit ledger, product-summary recompute,
 * open-order release-and-replan). Reject → marks the row `rejected`
 * with the reviewer's reason; no stock moves.
 *
 * Open-order safety: the approval card shows the affected orders
 * upfront. When any exist, approve requires the reviewer to tick
 * "Approve & release orders" before the auto-apply runs (mirrors
 * the direct-apply 409 pattern for full transparency).
 */
const STATUS_TABS = [
  { key: 'pending',   label: 'Pending',   icon: Clock,        pillClass: 'bg-amber-100 text-amber-800' },
  { key: 'approved',  label: 'Approved',  icon: CheckCircle2, pillClass: 'bg-emerald-100 text-emerald-800' },
  { key: 'rejected',  label: 'Rejected',  icon: XCircle,      pillClass: 'bg-rose-100 text-rose-800' },
  { key: 'cancelled', label: 'Cancelled', icon: Ban,          pillClass: 'bg-slate-100 text-slate-700' },
];

const REASON_LABELS = {
  count_correction: 'Count correction',
  damage: 'Damage',
  write_off: 'Write-off',
  found_stock: 'Found stock',
  expiry_discard: 'Expiry discard',
  cycle_count: 'Cycle count',
  system_correction: 'System correction',
  other: 'Other',
};

export default function AdminStockApprovals() {
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem('mr_admin_token') || ''}` }),
    [],
  );
  const [meLoaded, setMeLoaded] = useState(false);
  const [canApprove, setCanApprove] = useState(false);
  const [isSuper, setIsSuper] = useState(false);
  const [tab, setTab] = useState('pending');
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);   // request row
  const [rejectNote, setRejectNote] = useState('');
  const [approveFor, setApproveFor] = useState(null); // request row when confirmation needed
  // Feb 2026 — Bulk approve state (multi-select on Pending tab).
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(null); // { affected_orders, count } when a 409 needs a confirm.
  // Feb 2026 — History search (Adjustment No. + date range). Applies
  // to non-pending tabs where every approved row carries an ADJ-N.
  const [searchQ, setSearchQ] = useState('');
  const [searchExact, setSearchExact] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const token = localStorage.getItem('mr_admin_token') || '';
  const isHistoryTab = tab !== 'pending';

  useEffect(() => {
    (async () => {
      try {
        const me = await axios.get(`${API}/admin/me`, { headers: authHeaders });
        const superFlag = !!me.data?.is_super_admin;
        setIsSuper(superFlag);
        const perms = Array.isArray(me.data?.permissions) ? me.data.permissions : [];
        setCanApprove(superFlag || perms.includes('*') || perms.includes('warehouse.stock_adjust_approve'));
      } catch { /* leave defaults */ }
      setMeLoaded(true);
    })();
  }, [authHeaders]);

  const load = useCallback(async () => {
    if (!canApprove) return;
    setBusy(true);
    try {
      const params = { scope: 'all', status: tab, limit: 200 };
      // Feb 2026 — apply search filters only on non-pending tabs; the
      // approvals search is scoped to the History views.
      if (tab !== 'pending') {
        if (searchQ.trim()) {
          params.q = searchQ.trim();
          if (searchExact) params.exact = 'true';
        }
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      }
      const r = await axios.get(`${API}/admin/warehouse/stock-adjustment-requests`, {
        headers: authHeaders, params,
      });
      setRows(r.data?.requests || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load requests');
    } finally {
      setBusy(false);
    }
  }, [authHeaders, canApprove, tab, searchQ, searchExact, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);
  // Selection resets whenever we switch tab or reload rows so the
  // bulk-approve bar can't act on stale ids.
  useEffect(() => { setSelectedIds(new Set()); }, [tab]);

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllPending = () => {
    const pendingIds = rows.filter((r) => r.status === 'pending').map((r) => r.id);
    setSelectedIds(new Set(pendingIds));
  };
  const clearSelection = () => setSelectedIds(new Set());

  const bulkApprove = async ({ confirm = false } = {}) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/warehouse/stock-adjustment-requests/bulk-approve`,
        { request_ids: ids, note: '', confirm_release_orders: !!confirm },
        { headers: authHeaders },
      );
      toast.success(
        `Slip ${data.adjustment_no} approved — ${data.batch_size} request(s), `
        + `${data.total_adjustments} placement change(s).`
      );
      if ((data.orders_released_and_replanned || []).length) {
        toast.warning(`Released ${data.orders_released_and_replanned.length} order(s); auto re-planned.`);
      }
      setBulkConfirm(null);
      clearSelection();
      await load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det && typeof det === 'object' && det.code === 'open_orders_would_be_released') {
        setBulkConfirm({
          affected_orders: det.affected_orders || [],
          count: (det.affected_orders || []).length,
          request_count: ids.length,
        });
      } else if (e?.response?.status === 409 && det && typeof det === 'object' && det.code === 'bulk_partial_failure') {
        toast.error(
          `Partial failure at request ${(det.failing_request_id || '').slice(0, 8)}: `
          + (det.underlying?.message || 'unknown error')
        );
      } else {
        toast.error(typeof det === 'string' ? det : (det?.message || 'Bulk approve failed'));
      }
    } finally {
      setBulkBusy(false);
    }
  };

  const approve = async (row, { confirm = false } = {}) => {
    setBusyId(row.id);
    try {
      const { data } = await axios.post(
        `${API}/admin/warehouse/stock-adjustment-requests/${row.id}/approve`,
        { note: '', confirm_release_orders: !!confirm },
        { headers: authHeaders },
      );
      toast.success(`Approved — ${data.apply_result?.adjustments || 0} placement change(s) applied.`);
      if (data.apply_result?.adjustment_no) {
        toast.success(`Slip ${data.apply_result.adjustment_no} generated — download PDF from the Approved tab.`);
      }
      if ((data.apply_result?.merges || 0) > 0) {
        toast.success(`${data.apply_result.merges} pallet(s) merged successfully into existing location(s).`);
      }
      if ((data.apply_result?.orders_released_and_replanned || []).length) {
        toast.warning(`Released ${data.apply_result.orders_released_and_replanned.length} order(s); auto re-planned.`);
      }
      setApproveFor(null);
      await load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det && typeof det === 'object' && det.code === 'open_orders_would_be_released') {
        setApproveFor({ ...row, __ordersFromBackend: det.affected_orders || [] });
      } else {
        toast.error(typeof det === 'string' ? det : (det?.message || 'Approve failed'));
      }
    } finally {
      setBusyId(null);
    }
  };

  const submitReject = async () => {
    if (!rejectFor) return;
    const note = (rejectNote || '').trim();
    if (!note) { toast.error('Add a rejection reason first.'); return; }
    setBusyId(rejectFor.id);
    try {
      await axios.post(
        `${API}/admin/warehouse/stock-adjustment-requests/${rejectFor.id}/reject`,
        { note },
        { headers: authHeaders },
      );
      toast.success('Request rejected.');
      setRejectFor(null);
      setRejectNote('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    } finally {
      setBusyId(null);
    }
  };

  if (!meLoaded) {
    return (
      <AdminLayout>
        <div className="p-6 flex items-center gap-2 text-slate-500 text-sm" data-testid="stock-approvals-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      </AdminLayout>
    );
  }
  if (!canApprove) {
    return (
      <AdminLayout>
        <div className="p-6 max-w-lg mx-auto text-center" data-testid="stock-approvals-no-perm">
          <ShieldCheck className="w-12 h-12 mx-auto text-slate-300" />
          <h1 className="mt-3 text-lg font-bold text-slate-900">Stock Approvals</h1>
          <p className="mt-1 text-sm text-slate-500">
            You don't have the <code className="text-xs bg-slate-100 px-1 rounded">warehouse.stock_adjust_approve</code> permission. Ask a super admin to grant it.
          </p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-h-screen bg-slate-50 p-4 md:p-8" data-testid="stock-approvals-page">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-baseline justify-between gap-3 mb-4">
            <div>
              <h1 className="text-2xl font-black text-slate-900 flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-[#0078D4]" /> Stock Approvals
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">
                Review pending stock-adjustment requests. Approving auto-applies via the same engine that runs direct adjustments.
              </p>
            </div>
            <button
              type="button"
              onClick={load}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              data-testid="stock-approvals-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-4 overflow-x-auto" data-testid="stock-approvals-tabs">
            {STATUS_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm font-semibold border transition ${
                  tab === t.key
                    ? 'bg-[#0078D4] text-white border-[#0078D4]'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                }`}
                data-testid={`stock-approvals-tab-${t.key}`}
              >
                <t.icon className="w-4 h-4" />
                {t.label}
              </button>
            ))}
          </div>

          {/* Feb 2026 — Bulk-approve bar (Pending tab only). Emits ONE
              ADJ-NNNNN covering every selected request. */}
          {tab === 'pending' && (rows.length > 0 || selectedIds.size > 0) && (
            <div
              className={`mb-4 rounded-xl border p-3 shadow-sm flex flex-wrap items-center gap-3 ${
                selectedIds.size > 0
                  ? 'bg-blue-50 border-blue-200'
                  : 'bg-white border-slate-200'
              }`}
              data-testid="stock-approvals-bulk-bar"
            >
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rows.length > 0 && selectedIds.size === rows.filter((r) => r.status === 'pending').length}
                  onChange={(e) => (e.target.checked ? selectAllPending() : clearSelection())}
                  className="rounded border-slate-300"
                  data-testid="stock-approvals-bulk-select-all"
                />
                <span className="font-medium">
                  {selectedIds.size > 0
                    ? `${selectedIds.size} selected`
                    : 'Select all pending'}
                </span>
              </label>
              {selectedIds.size > 0 && (
                <>
                  <span className="text-slate-400">·</span>
                  <span className="text-xs text-slate-600">
                    Approving as one bundles them into a single <b>ADJ-NNNNN</b> slip and PDF.
                  </span>
                  <div className="ml-auto flex items-center gap-2">
                    <button
                      type="button"
                      onClick={clearSelection}
                      disabled={bulkBusy}
                      className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-60"
                      data-testid="stock-approvals-bulk-clear"
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      onClick={() => bulkApprove({ confirm: false })}
                      disabled={bulkBusy}
                      className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold text-white bg-[#0078D4] rounded-lg hover:bg-[#0068B8] disabled:opacity-60"
                      data-testid="stock-approvals-bulk-approve-btn"
                    >
                      {bulkBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                      <CheckCircle2 className="w-4 h-4" />
                      Approve as one slip
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Feb 2026 — History search bar. Shown on non-pending tabs
              since pending requests have no ADJ-N yet. */}
          {isHistoryTab && (
            <div className="mb-4 bg-white border border-slate-200 rounded-xl p-3 shadow-sm" data-testid="stock-approvals-search-bar">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                <div className="md:col-span-5">
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Adjustment No.</label>
                  <div className="mt-1 relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      data-testid="stock-approvals-search-input"
                      type="text"
                      value={searchQ}
                      onChange={(e) => setSearchQ(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && load()}
                      placeholder="ADJ-10001 (partial or full)"
                      className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0078D4] focus:border-[#0078D4] font-mono"
                    />
                  </div>
                  <label className="mt-1.5 flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
                    <input
                      data-testid="stock-approvals-search-exact"
                      type="checkbox"
                      checked={searchExact}
                      onChange={(e) => setSearchExact(e.target.checked)}
                      className="rounded border-slate-300"
                    />
                    Exact match
                  </label>
                </div>
                <div className="md:col-span-3">
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">From</label>
                  <div className="mt-1 relative">
                    <CalendarDays className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      data-testid="stock-approvals-date-from"
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0078D4] focus:border-[#0078D4]"
                    />
                  </div>
                </div>
                <div className="md:col-span-3">
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">To</label>
                  <div className="mt-1 relative">
                    <CalendarDays className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      data-testid="stock-approvals-date-to"
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0078D4] focus:border-[#0078D4]"
                    />
                  </div>
                </div>
                <div className="md:col-span-1 flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={load}
                    disabled={busy}
                    className="w-full px-3 py-2 text-sm font-medium text-white bg-slate-800 rounded-lg hover:bg-slate-900 disabled:opacity-60"
                    data-testid="stock-approvals-search-apply"
                  >
                    Apply
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSearchQ(''); setSearchExact(false); setDateFrom(''); setDateTo(''); setTimeout(load, 0); }}
                    className="w-full px-2 py-1 text-xs text-slate-500 hover:text-slate-700"
                    data-testid="stock-approvals-search-reset"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Rows */}
          {rows.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500 text-sm" data-testid="stock-approvals-empty">
              {busy ? 'Loading…' : `No ${tab} requests.`}
            </div>
          ) : (
            <div className="space-y-3" data-testid="stock-approvals-list">
              {rows.map((r) => (
                <RequestCard
                  key={r.id}
                  row={r}
                  busy={busyId === r.id}
                  isPending={r.status === 'pending'}
                  authToken={token}
                  selectable={r.status === 'pending'}
                  selected={selectedIds.has(r.id)}
                  onToggleSelect={() => toggleSelected(r.id)}
                  onApprove={() => approve(r)}
                  onReject={() => { setRejectFor(r); setRejectNote(''); }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Reject modal */}
      {rejectFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="stock-approvals-reject-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-5 shadow-xl">
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="w-5 h-5 text-rose-700" />
              <h2 className="text-lg font-bold text-slate-900">Reject request</h2>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Requester will see this reason. Nothing on the floor changes.
            </p>
            <textarea
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              rows={4}
              placeholder="e.g. Please recount before submitting — this doesn't match receiving log."
              className="w-full rounded-lg border-2 border-slate-200 focus:border-rose-500 focus:outline-none px-3 py-2 text-sm"
              data-testid="stock-approvals-reject-note"
            />
            <div className="mt-3 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => { setRejectFor(null); setRejectNote(''); }}
                className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitReject}
                disabled={busyId === rejectFor.id || !rejectNote.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-700 text-white text-sm font-bold hover:bg-rose-800 disabled:opacity-50"
                data-testid="stock-approvals-reject-submit"
              >
                {busyId === rejectFor.id && <Loader2 className="w-4 h-4 animate-spin" />}
                Reject request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve confirmation (only when backend flags open orders would be released) */}
      {approveFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="stock-approvals-approve-modal">
          <div className="bg-white rounded-xl max-w-lg w-full p-5 shadow-xl">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-amber-700" />
              <h2 className="text-lg font-bold text-slate-900">Approving will release open orders</h2>
            </div>
            <p className="text-xs text-slate-600 mb-3">
              These orders will be released and automatically re-planned onto surviving stock.
            </p>
            <ul className="space-y-1 max-h-40 overflow-y-auto bg-amber-50 border border-amber-200 rounded p-2" data-testid="stock-approvals-approve-affected">
              {(approveFor.__ordersFromBackend || approveFor.affected_orders_snapshot || []).map((o) => (
                <li key={o.order_id} className="text-xs font-mono text-amber-900">
                  #{o.order_number || o.order_id?.slice(0, 8)} — {o.qty_held} on this pallet
                </li>
              ))}
            </ul>
            <div className="mt-3 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setApproveFor(null)}
                className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => approve(approveFor, { confirm: true })}
                disabled={busyId === approveFor.id}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-700 text-white text-sm font-bold hover:bg-amber-800 disabled:opacity-50"
                data-testid="stock-approvals-approve-release-submit"
              >
                {busyId === approveFor.id && <Loader2 className="w-4 h-4 animate-spin" />}
                Approve &amp; release orders
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feb 2026 — Bulk-approve open-orders confirmation modal. Same
          shape as the single-approve modal above. */}
      {bulkConfirm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="stock-approvals-bulk-modal">
          <div className="bg-white rounded-xl max-w-lg w-full p-5 shadow-xl">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-amber-700" />
              <h2 className="text-lg font-bold text-slate-900">
                Approving {bulkConfirm.request_count} request(s) will release {bulkConfirm.count} open order(s)
              </h2>
            </div>
            <p className="text-xs text-slate-600 mb-3">
              A single ADJ-NNNNN slip will be generated covering every request. The listed orders will be released and automatically re-planned onto surviving stock.
            </p>
            <ul className="space-y-1 max-h-40 overflow-y-auto bg-amber-50 border border-amber-200 rounded p-2" data-testid="stock-approvals-bulk-affected">
              {(bulkConfirm.affected_orders || []).map((o) => (
                <li key={o.order_id} className="text-xs font-mono text-amber-900">
                  #{o.order_number || o.order_id?.slice(0, 8)} — {o.qty_held}
                </li>
              ))}
            </ul>
            <div className="mt-3 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setBulkConfirm(null)}
                className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => bulkApprove({ confirm: true })}
                disabled={bulkBusy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-700 text-white text-sm font-bold hover:bg-amber-800 disabled:opacity-50"
                data-testid="stock-approvals-bulk-release-submit"
              >
                {bulkBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                Approve &amp; release orders
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function RequestCard({ row, busy, isPending, authToken, selectable, selected, onToggleSelect, onApprove, onReject }) {
  const affected = row.affected_orders_snapshot || [];
  const impact = row.impact_snapshot || [];
  const status = row.status || 'pending';
  const tab = STATUS_TABS.find((t) => t.key === status) || STATUS_TABS[0];
  const adjNo = row.adjustment_no || row.apply_result?.adjustment_no || '';
  const slipId = row.slip_id || row.apply_result?.slip_id || '';
  const batchSize = row.apply_result?.batch_size || 0;
  const openPdf = () => {
    if (!slipId) return;
    // Backend uses `require_admin_flex` — token via query string keeps
    // new-tab downloads working (browser can't attach auth headers).
    const url = `${API}/admin/stock-adjustment-slips/${slipId}/pdf?token=${encodeURIComponent(authToken || '')}`;
    window.open(url, '_blank', 'noopener');
  };
  return (
    <div
      className={`bg-white rounded-xl border p-4 ${
        selected ? 'border-[#0078D4] ring-2 ring-blue-100' : 'border-slate-200'
      }`}
      data-testid={`stock-approvals-row-${row.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1 flex items-start gap-2">
          {/* Feb 2026 — pending rows carry a checkbox for the
              bulk-approve flow. Hidden on non-pending tabs. */}
          {selectable && (
            <input
              type="checkbox"
              checked={!!selected}
              onChange={onToggleSelect}
              className="mt-1 rounded border-slate-300"
              data-testid={`stock-approvals-select-${row.id}`}
              aria-label="Select for bulk approval"
            />
          )}
          <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${tab.pillClass}`}>
              <tab.icon className="w-3 h-3" /> {tab.label}
            </span>
            {adjNo && (
              <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-mono font-bold text-[#0078D4] bg-blue-50 border border-blue-200"
                data-testid={`stock-approvals-adj-no-${row.id}`}
              >
                {adjNo}
              </span>
            )}
            {batchSize > 1 && (
              <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold text-purple-800 bg-purple-50 border border-purple-200"
                data-testid={`stock-approvals-batch-${row.id}`}
                title={`Bundled into a single slip with ${batchSize - 1} other request(s)`}
              >
                Bundle × {batchSize}
              </span>
            )}
            <span className="text-xs text-slate-500">
              #{(row.id || '').slice(0, 8)} · {REASON_LABELS[row.reason_code] || row.reason_code}
            </span>
          </div>
          <div className="mt-2 text-xs text-slate-600 flex items-center gap-1.5 flex-wrap">
            <User className="w-3.5 h-3.5 text-slate-400" />
            <b className="text-slate-800">{row.requested_by_name}</b>
            <span className="text-slate-400">·</span>
            <span>{new Date(row.created_at).toLocaleString()}</span>
          </div>
          {row.note && (
            <div className="mt-2 text-sm text-slate-700 italic">
              &ldquo;{row.note}&rdquo;
            </div>
          )}
          </div>
        </div>
        {/* PDF download for approved rows carrying a slip. */}
        {status === 'approved' && slipId && (
          <button
            type="button"
            onClick={openPdf}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-50 text-sm font-semibold text-[#0078D4] hover:bg-blue-100"
            data-testid={`stock-approvals-download-pdf-${row.id}`}
            title={`Download ${adjNo} slip`}
          >
            <FileDown className="w-4 h-4" />
            Download PDF
          </button>
        )}
      </div>

      {/* Operations preview */}
      <div className="mt-3 rounded-lg bg-slate-50 border border-slate-100 divide-y divide-slate-100" data-testid={`stock-approvals-ops-${row.id}`}>
        {impact.length === 0 ? (
          <div className="p-2 text-xs text-slate-500">No preview data.</div>
        ) : impact.map((i, idx) => (
          <div key={idx} className="p-2 text-xs" data-testid={`stock-approvals-op-${row.id}-${idx}`}>
            {/* Feb 2026 — user directive: SKU + item name must appear
                on every impact row. Backend now enriches
                `impact_snapshot` with sku/product_name/uom/pack.
                When both come back empty (product deleted from
                catalog), we still render a placeholder so the row
                stays clearly identifiable. */}
            <div className="mb-1 flex items-center gap-1.5 flex-wrap" data-testid={`stock-approvals-op-item-${row.id}-${idx}`}>
              {i.sku || i.product_name ? (
                <>
                  <span className="font-mono font-bold text-slate-900">{i.sku || '—'}</span>
                  <span className="text-slate-400">·</span>
                  <span className="text-slate-800 font-medium">{i.product_name || '—'}</span>
                  {(i.pack || i.uom) && (
                    <span className="text-slate-500">
                      {i.pack && <>({i.pack})</>}
                      {i.pack && i.uom && ' · '}
                      {i.uom}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-xs font-mono italic text-slate-500">
                  [Product #{(i.product_id || '').slice(0, 8) || '—'} — not found in catalog]
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap text-slate-600">
              <Package className="w-3.5 h-3.5 text-slate-400" />
              <span className="font-mono">{i.pallet_id || '—'}</span>
              <span className="text-slate-400">·</span>
              <span>{i.location_id || '—'}</span>
              <span className="text-slate-400 ml-1">Qty</span>
              <b className="text-slate-900 tabular-nums">{i.before_qty}</b>
              <ArrowRight className="w-3 h-3 text-slate-400" />
              <b className={`tabular-nums ${i.delta > 0 ? 'text-emerald-700' : i.delta < 0 ? 'text-rose-700' : 'text-slate-700'}`}>{i.target_qty}</b>
              <span className={`text-[11px] ml-0.5 tabular-nums ${i.delta > 0 ? 'text-emerald-700' : i.delta < 0 ? 'text-rose-700' : 'text-slate-500'}`}>
                ({i.delta > 0 ? '+' : ''}{i.delta})
              </span>
            </div>
            {(i.location_changed || i.pallet_changed) && (
              <div
                className="mt-1 flex items-center gap-2 flex-wrap text-[11px] font-semibold text-indigo-800 bg-indigo-50 border border-indigo-200 rounded px-2 py-1"
                data-testid={`stock-approvals-move-${row.id}-${idx}`}
              >
                <ArrowRight className="w-3 h-3 text-indigo-500" />
                Move
                {i.pallet_changed && (
                  <span className="font-mono">
                    pallet <b>{i.pallet_id || '—'}</b> → <b>{i.target_pallet_id || '—'}</b>
                  </span>
                )}
                {i.pallet_changed && i.location_changed && <span className="text-indigo-400">·</span>}
                {i.location_changed && (
                  <span className="font-mono">
                    location <b>{i.location_id || '—'}</b> → <b>{i.target_location_id || '—'}</b>
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Affected orders */}
      {affected.length > 0 && (
        <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-2" data-testid={`stock-approvals-affected-${row.id}`}>
          <div className="flex items-center gap-1.5 text-xs font-bold text-amber-900">
            <AlertTriangle className="w-3.5 h-3.5" />
            Approving releases {affected.length} open order(s)
          </div>
          <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
            {affected.map((o) => (
              <li key={o.order_id} className="text-[11px] font-mono text-amber-900">
                #{o.order_number || o.order_id?.slice(0, 8)}<span className="text-amber-700"> · {o.qty_held}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Review outcome */}
      {row.reviewed_at && (
        <div className="mt-3 text-xs text-slate-600">
          <b>{row.reviewed_by_name}</b> {status === 'rejected' ? 'rejected' : status === 'cancelled' ? 'cancelled' : 'approved'} on {new Date(row.reviewed_at).toLocaleString()}
          {row.review_note && <span className="italic"> — &ldquo;{row.review_note}&rdquo;</span>}
        </div>
      )}

      {/* Actions (only for pending) */}
      {isPending && (
        <div className="mt-3 flex gap-2 justify-end">
          <button
            type="button"
            onClick={onReject}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-rose-300 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
            data-testid={`stock-approvals-reject-btn-${row.id}`}
          >
            <XCircle className="w-4 h-4" /> Reject
          </button>
          <button
            type="button"
            onClick={onApprove}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-sm font-bold hover:bg-emerald-800 disabled:opacity-50"
            data-testid={`stock-approvals-approve-btn-${row.id}`}
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            <CheckCircle2 className="w-4 h-4" /> {affected.length > 0 ? 'Approve…' : 'Approve'}
          </button>
        </div>
      )}
    </div>
  );
}
